import { Award, Plus } from 'lucide-react';
import * as React from 'react';
import {
  RECOGNITION_LABELS,
  RECOGNITION_TIER_LABELS,
  isOfficialRecognition,
  recognitionTypesFor,
  type RecognitionType,
} from '@cfl/shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { StarArt } from '@/components/illustrations';
import { PageContainer } from '@/components/shell/page-container';
import { PILL_CLASS } from '@/lib/badges';
import { useGiveRecognition, useLeaderboard, useRecognitionFeed } from '@/lib/engagement-api';
import { useDirectory, type DirectoryEntry } from '@/lib/people-api';
import { useCurrentUser } from '@/lib/session';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

function GiveDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const me = useCurrentUser();
  /**
   * The whole recipient, not just their id.
   *
   * The picker searches server-side and holds only the people matching what was
   * typed, so re-deriving the recipient from that list would lose them the moment
   * the search box changed — and with them `canGrantOfficial` below, which would
   * disarm a manager's own award for a reason nothing on screen explains.
   */
  const [recipient, setRecipient] = React.useState<DirectoryEntry | null>(null);
  const [type, setType] = React.useState<RecognitionType>('STAR_PERFORMER');
  const [message, setMessage] = React.useState('');
  const [search, setSearch] = React.useState('');
  // /people/directory, NOT /people: the HR directory is role-gated, so a plain
  // employee got a 403 here and an empty "Who" list - unable to recognise anyone,
  // in the one feature that is explicitly open to everybody.
  const { data: people, isFetching, error: searchError } = useDirectory(search || undefined);
  const give = useGiveRecognition();

  const toEmployeeId = recipient?.id ?? '';
  const isHr = me.role === 'HR' || me.role === 'SUPER_ADMIN';
  // Whoever is on offer right now, plus the person already picked so the field never
  // reads as empty after the search moves on.
  const candidates = React.useMemo(() => {
    const found = (people ?? []).filter((p) => p.id !== me.id);
    return recipient && !found.some((p) => p.id === recipient.id) ? [recipient, ...found] : found;
  }, [people, me.id, recipient]);
  /**
   * Official awards are HR's, or this person's own manager's — the same identity
   * rule the server applies. Recomputed as the recipient changes, because a manager
   * may award their own report and nobody else.
   */
  const canGrantOfficial = isHr || (recipient?.reportingManagerId === me.id && recipient !== undefined);

  // Selecting someone you cannot officially award must not leave an official type
  // armed in the picker — it would fail on submit for a reason nothing showed.
  React.useEffect(() => {
    if (!canGrantOfficial && isOfficialRecognition(type)) setType('STAR_PERFORMER');
  }, [canGrantOfficial, type]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void give
              .mutateAsync({ toEmployeeId, type, message })
              .then(() => {
                toast.success('Recognition sent');
                setRecipient(null);
                setMessage('');
                onOpenChange(false);
              })
              .catch((x: unknown) => toast.error('Could not send', { description: err(x) }));
          }}
        >
          <DialogHeader>
            <DialogTitle>Recognise someone</DialogTitle>
            <DialogDescription>
              They will be notified. Neither kind carries a rating or feeds an appraisal score —
              an official award appears on the appraisal as evidence, not as points.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5" data-guide="recognition-to">
              <Label htmlFor="recipient">Who</Label>
              <Combobox
                id="recipient"
                value={toEmployeeId || null}
                // Select-only: recognition goes to a colleague who already exists.
                onChange={(id) => setRecipient(candidates.find((p) => p.id === id) ?? null)}
                options={candidates.map((p) => ({
                  value: p.id,
                  label: p.name,
                  description: p.email,
                  hint: p.designationTitle ?? undefined,
                }))}
                onSearchChange={setSearch}
                loading={isFetching}
                error={searchError ? 'Could not search people. Try again.' : null}
                placeholder="Select a colleague"
                searchPlaceholder="Search by name or email"
                emptyText="Nobody matches"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Award</Label>
              <Select value={type} onValueChange={(v) => setType(v as RecognitionType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>{RECOGNITION_TIER_LABELS.APPRECIATION.label}</SelectLabel>
                    {recognitionTypesFor('APPRECIATION').map((t) => (
                      <SelectItem key={t} value={t}>
                        {RECOGNITION_LABELS[t].emoji} {RECOGNITION_LABELS[t].label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                  {canGrantOfficial && (
                    <SelectGroup>
                      <SelectLabel>{RECOGNITION_TIER_LABELS.OFFICIAL.label}</SelectLabel>
                      {recognitionTypesFor('OFFICIAL').map((t) => (
                        <SelectItem key={t} value={t}>
                          {RECOGNITION_LABELS[t].emoji} {RECOGNITION_LABELS[t].label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {isOfficialRecognition(type)
                  ? RECOGNITION_TIER_LABELS.OFFICIAL.blurb
                  : RECOGNITION_TIER_LABELS.APPRECIATION.blurb}
              </p>
            </div>

            <div className="space-y-1.5" data-guide="recognition-message">
              <Label htmlFor="msg">What did they do?</Label>
              <Textarea
                id="msg"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Be specific — a vague thank-you is worth less than none."
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Send the recognition"} data-guide="recognition-send" type="submit" disabled={!toEmployeeId || !message.trim() || give.isPending}>
              Send
            </Button>
            {(!toEmployeeId || !message.trim()) && <span className="self-center text-xs text-muted-foreground">{!toEmployeeId ? 'Choose who you are recognising' : 'Write a message'}</span>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function RecognitionPage() {
  const [open, setOpen] = React.useState(false);
  const { data: feed, isPending } = useRecognitionFeed();
  const { data: leaderboard } = useLeaderboard();

  const all = feed ?? [];
  // Same rule the server authorizes on, applied to what is already loaded.
  const official = all.filter((r) => isOfficialRecognition(r.type));
  const appreciation = all.filter((r) => !isOfficialRecognition(r.type));

  return (
    <PageContainer>
      <div className="flex justify-end">
        <Button hint={"Appreciate a colleague's work"} className="gap-2" data-guide="recognition-give" onClick={() => setOpen(true)}>
          <Plus className="size-4" strokeWidth={2.4} />
          Recognise someone
        </Button>
      </div>

      {official.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Award className="size-4 text-primary" />
              Official awards
            </CardTitle>
            <CardDescription>{RECOGNITION_TIER_LABELS.OFFICIAL.blurb}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {official.slice(0, 6).map((r) => (
              <div key={r.id} className="flex gap-3 rounded-md border border-primary/30 p-3">
                <span className="text-xl" aria-hidden>
                  {RECOGNITION_LABELS[r.type].emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{r.toEmployee.name}</p>
                  <p className="text-xs text-primary">{RECOGNITION_LABELS[r.type].label}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{r.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Awarded by {r.fromEmployee.name} ·{' '}
                    {new Date(r.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <Card>
          <CardHeader>
            <CardTitle>Appreciation</CardTitle>
            <CardDescription>
              Across the whole company · {RECOGNITION_TIER_LABELS.APPRECIATION.blurb}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {isPending && <Skeleton className="h-32 w-full" />}
            {!isPending && appreciation.length === 0 && (
              <EmptyState
                art={StarArt}
                title="No appreciation yet"
                body="Anyone can recognise anyone — it is not limited to managers."
              />
            )}
            {appreciation.map((r) => (
              <div key={r.id} className="flex gap-3 rounded-md border p-3">
                <span className="text-xl" aria-hidden>
                  {RECOGNITION_LABELS[r.type].emoji}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm">
                    <span className="font-medium">{r.fromEmployee.name}</span>
                    <span className="text-muted-foreground"> recognised </span>
                    <span className="font-medium">{r.toEmployee.name}</span>
                  </p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{r.message}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {RECOGNITION_LABELS[r.type].label} ·{' '}
                    {new Date(r.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Leaderboard</CardTitle>
            <CardDescription>
              By peer appreciation received. Official awards are excluded — they would
              count the same as a thank-you and rank the same people twice.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(leaderboard ?? []).length === 0 && (
              <EmptyState size="sm" art={StarArt} title="Nothing yet" />
            )}
            {(leaderboard ?? []).map((row, i) => (
              <div key={row.employeeId} className="flex items-center gap-3">
                <span className="pms-num w-5 text-sm text-muted-foreground">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{row.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {row.departmentName ?? '—'}
                  </p>
                </div>
                <Badge variant="soft-success" className={PILL_CLASS}>
                  {row.count}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <GiveDialog open={open} onOpenChange={setOpen} />
    </PageContainer>
  );
}
